/**
 * Phase 15.6Q — Producer Dashboard.
 *
 * Plays / Downloads / Sales / Earnings / Pending Payouts / Lifetime Revenue.
 * Plus recent sales table + top beats. Auth required.
 */
import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import api from "@/lib/api";
import SharedNavigation from "@/components/SharedNavigation";
import StripeConnectCard from "@/components/StripeConnectCard";
import { optimizeImage } from "@/lib/mediaUrls";

const formatUSD = (n) =>
  new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 2 }).format(Number(n) || 0);

const StatTile = ({ icon, label, value, sublabel, accent = "purple", testId }) => (
  <Card className={`p-4 bg-gray-950/70 border-${accent}-500/20`} data-testid={testId}>
    <div className="flex items-start gap-3">
      <span className="text-3xl flex-shrink-0">{icon}</span>
      <div className="min-w-0">
        <div className="text-[10px] uppercase tracking-wider text-gray-400">{label}</div>
        <div className={`text-2xl font-black text-${accent}-100 leading-none mt-1`}>
          {value}
        </div>
        {sublabel && <div className="text-[11px] text-gray-500 mt-1">{sublabel}</div>}
      </div>
    </div>
  </Card>
);

const ProducerDashboardPage = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [payoutAmount, setPayoutAmount] = useState("");
  const [payoutMethod, setPayoutMethod] = useState("paypal");
  const [payoutHandle, setPayoutHandle] = useState("");
  const [payoutLegalName, setPayoutLegalName] = useState("");
  const [payoutNotes, setPayoutNotes] = useState("");
  const [payoutBusy, setPayoutBusy] = useState(false);
  const [recentRequests, setRecentRequests] = useState([]);
  // Phase 17.1 — Manage Beats panel state
  const [myBeats, setMyBeats] = useState([]);
  const [beatsLoading, setBeatsLoading] = useState(false);
  const [showHidden, setShowHidden] = useState(false);

  const loadMyBeats = async () => {
    setBeatsLoading(true);
    try {
      const r = await api.get(`/beats/me/list?include_hidden=true`);
      setMyBeats(r?.data?.beats || []);
    } catch (_e) {
      // optional endpoint — falls back to empty
      setMyBeats([]);
    } finally {
      setBeatsLoading(false);
    }
  };

  useEffect(() => { if (user?.user_id) loadMyBeats(); }, [user?.user_id]);

  const hideBeat = async (bid, title) => {
    if (!window.confirm(`Hide "${title}" from the marketplace? You can restore it anytime.`)) return;
    try {
      await api.delete(`/beats/${bid}`);
      setMyBeats((p) => p.map((b) => (b.beat_id === bid ? { ...b, hidden: true } : b)));
    } catch (e) {
      const d = e?.response?.data?.detail;
      alert(typeof d === "string" ? d : "Couldn't hide beat");
    }
  };

  const restoreBeat = async (bid) => {
    try {
      await api.post(`/beats/${bid}/restore`);
      setMyBeats((p) => p.map((b) => (b.beat_id === bid ? { ...b, hidden: false } : b)));
    } catch (e) {
      const d = e?.response?.data?.detail;
      alert(typeof d === "string" ? d : "Couldn't restore beat");
    }
  };

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const me = await api.get("/auth/me");
        const u = me.data?.user || me.data;
        if (!u || !u.user_id) { navigate("/login?next=/producer"); return; }
        if (alive) setUser(u);
        const [dash, hist] = await Promise.allSettled([
          api.get("/beats/me/dashboard"),
          api.get("/payouts/me/history?limit=10"),
        ]);
        if (alive && dash.status === "fulfilled") setData(dash.value.data);
        if (alive && hist.status === "fulfilled") setRecentRequests(hist.value.data?.requests || []);
      } catch (_e) {
        if (alive) navigate("/login?next=/producer");
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [navigate]);

  const requestPayout = async () => {
    const amt = Number(payoutAmount);
    if (!amt || amt <= 0) return;
    if (!payoutLegalName.trim()) {
      import("sonner").then(({ toast }) => toast.error("Legal name is required"));
      return;
    }
    if (!payoutHandle.trim()) {
      import("sonner").then(({ toast }) => toast.error("Payout handle / email is required"));
      return;
    }
    setPayoutBusy(true);
    try {
      // Phase 15.6Q — beat producer payout (USD, source='beat_sales')
      await api.post("/payouts/me/request-beats", {
        amount_usd: amt,
        method: payoutMethod,
        handle: payoutHandle.trim(),
        legal_name: payoutLegalName.trim(),
        notes: payoutNotes.trim() || null,
      });
      const [dash, hist] = await Promise.all([
        api.get("/beats/me/dashboard"),
        api.get("/payouts/me/history?limit=10"),
      ]);
      setData(dash.data);
      setRecentRequests(hist.data?.requests || []);
      setPayoutAmount(""); setPayoutNotes("");
      import("sonner").then(({ toast }) => toast.success("Payout requested — admin will review shortly"));
    } catch (e) {
      import("sonner").then(({ toast }) => toast.error(e?.response?.data?.detail || "Payout request failed"));
    } finally {
      setPayoutBusy(false);
    }
  };

  if (loading || !user) {
    return (
      <div className="min-h-screen bg-[#08080c] flex items-center justify-center text-gray-400">
        <div className="inline-block animate-spin rounded-full h-10 w-10 border-t-2 border-purple-500" />
      </div>
    );
  }

  const d = data || {};

  return (
    <div className="min-h-screen bg-[#08080c]" data-testid="producer-dashboard-page">
      <SharedNavigation currentUser={user} />

      <main className="max-w-6xl mx-auto px-4 sm:px-6 pt-24 pb-12">
        <div className="flex items-start justify-between flex-wrap gap-4 mb-8">
          <div>
            <span className="text-xs uppercase tracking-[0.3em] text-amber-300 font-bold">💎 RGM Producer · Beat Marketplace</span>
            <h1 className="text-4xl sm:text-5xl font-black text-white tracking-tight mt-1">Producer Dashboard</h1>
            <p className="text-gray-400 text-sm mt-2">
              Track your <strong className="text-amber-200">beat sales</strong>, earnings, and payouts. RGM platform fee: <span className="text-amber-300 font-bold">{d.platform_fee_pct ?? 10}%</span>.
              {" "}
              <Link to="/payouts" className="text-pink-300 underline decoration-dotted">
                Live/gift payouts? Go to /payouts →
              </Link>
            </p>
          </div>
          <div className="flex gap-2">
            <Link to="/submit-beat">
              <Button className="bg-gradient-to-r from-purple-600 to-pink-600 text-white" data-testid="dashboard-upload-cta">+ Upload Beat</Button>
            </Link>
            <Link to="/beats">
              <Button variant="outline" className="border-purple-500/40 text-purple-100" data-testid="dashboard-market-cta">Marketplace</Button>
            </Link>
          </div>
        </div>

        {/* STATS GRID */}
        <section className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 mb-10" data-testid="producer-stats-grid">
          <StatTile icon="🎹" label="Beats" value={(d.beats_count || 0).toLocaleString()} accent="purple" testId="stat-beats" />
          <StatTile icon="▶️" label="Plays" value={(d.plays_total || 0).toLocaleString()} accent="purple" testId="stat-plays" />
          <StatTile icon="⬇️" label="Downloads" value={(d.downloads_total || 0).toLocaleString()} accent="emerald" testId="stat-downloads" />
          <StatTile icon="💰" label="Sales" value={(d.sales_total || 0).toLocaleString()} accent="amber" testId="stat-sales" />
          <StatTile
            icon="🪙"
            label="Pending Earnings"
            value={formatUSD(d.pending_balance_usd)}
            sublabel={`Available to request`}
            accent="amber"
            testId="stat-pending"
          />
          <StatTile
            icon="🏆"
            label="Lifetime Revenue"
            value={formatUSD(d.lifetime_earnings_usd)}
            sublabel={`After ${d.platform_fee_pct ?? 10}% RGM fee`}
            accent="pink"
            testId="stat-lifetime"
          />
        </section>

        {/* PAYOUT REQUEST */}
        <section className="mb-10" data-testid="payout-section">
          <Card className="p-5 bg-gradient-to-br from-amber-500/10 via-orange-500/5 to-black border-amber-500/20">
            <div className="flex items-end justify-between flex-wrap gap-3 mb-4">
              <div>
                <h2 className="text-xl font-bold text-white">Request a payout</h2>
                <p className="text-sm text-gray-400 mt-1">
                  Available: <span className="text-amber-300 font-bold">{formatUSD(d.pending_balance_usd)}</span>
                  {(d.pending_payouts_usd || 0) > 0 && (
                    <span> · In flight: <span className="text-purple-200">{formatUSD(d.pending_payouts_usd)}</span></span>
                  )}
                </p>
              </div>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-3">
              <div className="lg:col-span-1">
                <label className="block text-[11px] uppercase tracking-wider text-gray-400 mb-1">Amount (USD)</label>
                <input
                  type="number" min="10" step="0.01"
                  value={payoutAmount}
                  onChange={(e) => setPayoutAmount(e.target.value)}
                  placeholder="0.00"
                  className="w-full bg-black/60 border border-amber-500/30 rounded-md text-white px-3 py-2 text-sm"
                  data-testid="payout-amount-input"
                  disabled={payoutBusy}
                />
              </div>
              <div className="lg:col-span-1">
                <label className="block text-[11px] uppercase tracking-wider text-gray-400 mb-1">Method</label>
                <select
                  value={payoutMethod}
                  onChange={(e) => setPayoutMethod(e.target.value)}
                  className="w-full bg-black/60 border border-amber-500/30 rounded-md text-white px-3 py-2 text-sm"
                  data-testid="payout-method-select"
                  disabled={payoutBusy}
                >
                  <option value="paypal">PayPal</option>
                  <option value="zelle">Zelle</option>
                  <option value="cashapp">Cash App</option>
                  <option value="bank">Bank Transfer</option>
                  <option value="usdt">USDT</option>
                </select>
              </div>
              <div className="lg:col-span-1">
                <label className="block text-[11px] uppercase tracking-wider text-gray-400 mb-1">Legal name</label>
                <input
                  type="text"
                  value={payoutLegalName}
                  onChange={(e) => setPayoutLegalName(e.target.value)}
                  placeholder="As on ID"
                  className="w-full bg-black/60 border border-amber-500/30 rounded-md text-white px-3 py-2 text-sm"
                  data-testid="payout-legal-name-input"
                  disabled={payoutBusy}
                />
              </div>
              <div className="lg:col-span-2">
                <label className="block text-[11px] uppercase tracking-wider text-gray-400 mb-1">Handle / email / account</label>
                <input
                  type="text"
                  value={payoutHandle}
                  onChange={(e) => setPayoutHandle(e.target.value)}
                  placeholder="user@example.com"
                  className="w-full bg-black/60 border border-amber-500/30 rounded-md text-white px-3 py-2 text-sm"
                  data-testid="payout-handle-input"
                  disabled={payoutBusy}
                />
              </div>
            </div>
            <div className="flex items-end justify-between gap-3 mt-3">
              <p className="text-[11px] text-gray-500">
                Minimum $10 · Admin will review within 24–48h · You&apos;ll get a notification on status changes.
              </p>
              <Button
                onClick={requestPayout}
                disabled={payoutBusy || !payoutAmount || Number(payoutAmount) <= 0 || Number(payoutAmount) > (d.pending_balance_usd || 0)}
                className="bg-amber-500 hover:bg-amber-400 text-black font-bold"
                data-testid="payout-request-button"
              >
                {payoutBusy ? "Requesting…" : "Request payout"}
              </Button>
            </div>
          </Card>
        </section>

        {/* PAYOUT HISTORY (always rendered so the testid is stable; shows empty state when no requests) */}
        <section className="mb-10" data-testid="payout-history-section">
          <h2 className="text-xl font-bold text-white mb-3">Payout history</h2>
          {recentRequests.length === 0 ? (
            <Card className="p-5 text-center text-gray-500 bg-gray-950/40 border-gray-800">
              No payout requests yet. Once you have pending earnings, request a payout above.
            </Card>
          ) : (
            <Card className="bg-gray-950/60 border-purple-500/20 divide-y divide-white/5 overflow-hidden">
              {recentRequests.map((p) => {
                const statusStyle = {
                  pending:  "bg-amber-500/20 text-amber-200 border-amber-500/40",
                  approved: "bg-sky-500/20 text-sky-200 border-sky-500/40",
                  paid:     "bg-emerald-500/20 text-emerald-200 border-emerald-500/40",
                  denied:   "bg-rose-500/20 text-rose-200 border-rose-500/40",
                }[p.status] || "bg-gray-700/30 text-gray-300";
                return (
                  <div key={p.request_id} className="p-4 flex items-center justify-between gap-3 text-sm" data-testid={`payout-row-${p.request_id}`}>
                    <div className="min-w-0">
                      <div className="font-bold text-white">{formatUSD(p.amount_usd)} via {p.method}</div>
                      <div className="text-[11px] text-gray-400">
                        {new Date(p.created_at).toLocaleString()} · {p.source === "beat_sales" ? "Beat sales" : "Gifts"}
                        {p.admin_notes ? <span className="text-rose-300/80"> · {p.admin_notes}</span> : null}
                      </div>
                    </div>
                    <span className={`text-[10px] uppercase tracking-wider px-2 py-1 rounded-full border font-bold ${statusStyle}`} data-testid={`payout-status-${p.request_id}`}>
                      {p.status}
                    </span>
                  </div>
                );
              })}
            </Card>
          )}
        </section>

        <div className="grid lg:grid-cols-2 gap-8">
          {/* MANAGE BEATS (Phase 17.1) */}
          <section className="lg:col-span-2" data-testid="manage-beats-section">
            <div className="flex items-end justify-between gap-3 flex-wrap mb-3">
              <h2 className="text-xl font-bold text-white">Manage my beats</h2>
              <div className="flex items-center gap-3">
                <label className="text-xs text-gray-400 flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={showHidden}
                    onChange={(e) => setShowHidden(e.target.checked)}
                    data-testid="manage-beats-show-hidden-toggle"
                  />
                  Show hidden
                </label>
                <Button
                  size="sm"
                  onClick={() => navigate("/submit-beat")}
                  className="bg-gradient-to-r from-purple-600 to-pink-600 text-white text-xs"
                  data-testid="manage-beats-upload-btn"
                >
                  + Upload new beat
                </Button>
              </div>
            </div>
            {beatsLoading ? (
              <Card className="p-6 text-center text-gray-500 bg-gray-950/40 border-gray-800">Loading…</Card>
            ) : (() => {
              const visible = (myBeats || []).filter((b) => showHidden || !b.hidden);
              if (!visible.length) {
                return (
                  <Card className="p-6 text-center text-gray-500 bg-gray-950/40 border-gray-800">
                    {myBeats.length ? "No matching beats — toggle 'Show hidden' to see archived." : "Upload your first beat to see it listed here."}
                  </Card>
                );
              }
              return (
                <Card className="bg-gray-950/60 border-purple-500/20 divide-y divide-white/5">
                  {visible.map((b) => (
                    <div
                      key={b.beat_id}
                      className="p-3 flex items-center gap-3"
                      data-testid={`manage-beat-row-${b.beat_id}`}
                    >
                      <div className="w-12 h-12 rounded-md overflow-hidden bg-purple-900/30 flex-shrink-0">
                        {b.cover_url
                          ? <img src={optimizeImage(b.cover_url, 'thumb')} alt={b.title} className="w-full h-full object-cover" onError={(e) => { e.target.src = "/rgm-logo.png"; }} />
                          : <div className="w-full h-full flex items-center justify-center text-xl">🎹</div>}
                      </div>
                      <div className="flex-1 min-w-0">
                        <Link to={`/beats/${b.beat_id}`} className="font-semibold text-white truncate hover:text-pink-300 block">{b.title}</Link>
                        <p className="text-[11px] text-gray-400">
                          {formatUSD(b.price || 0)} · {(b.plays || 0).toLocaleString()} plays
                          {b.hidden && <span className="ml-2 text-rose-300 font-bold">· HIDDEN</span>}
                        </p>
                      </div>
                      <div className="flex gap-1.5">
                        <Button
                          size="sm"
                          onClick={() => navigate(`/submit-beat/${b.beat_id}`)}
                          className="bg-purple-500/30 hover:bg-purple-500/50 border border-purple-400/40 text-purple-100 text-[11px] h-8"
                          data-testid={`manage-beat-edit-${b.beat_id}`}
                        >
                          ✏ Edit
                        </Button>
                        {b.hidden ? (
                          <Button
                            size="sm"
                            onClick={() => restoreBeat(b.beat_id)}
                            className="bg-emerald-500/30 hover:bg-emerald-500/50 border border-emerald-400/40 text-emerald-100 text-[11px] h-8"
                            data-testid={`manage-beat-restore-${b.beat_id}`}
                          >
                            ↩ Restore
                          </Button>
                        ) : (
                          <Button
                            size="sm"
                            onClick={() => hideBeat(b.beat_id, b.title)}
                            className="bg-rose-500/30 hover:bg-rose-500/50 border border-rose-400/40 text-rose-100 text-[11px] h-8"
                            data-testid={`manage-beat-hide-${b.beat_id}`}
                          >
                            🚫 Hide
                          </Button>
                        )}
                      </div>
                    </div>
                  ))}
                </Card>
              );
            })()}
          </section>

          {/* RECENT SALES */}
          <section data-testid="recent-sales-section">
            <h2 className="text-xl font-bold text-white mb-3">Recent sales</h2>
            {d.recent_sales?.length ? (
              <Card className="bg-gray-950/60 border-purple-500/20 divide-y divide-white/5">
                {d.recent_sales.map((s) => (
                  <div key={s.sale_id} className="p-4 flex items-center justify-between gap-3" data-testid={`recent-sale-${s.sale_id}`}>
                    <div className="min-w-0">
                      <Link to={`/beats/${s.beat_id}`} className="font-bold text-white hover:text-pink-300 truncate block">{s.beat_title || s.beat_id}</Link>
                      <p className="text-[11px] text-gray-400">{s.tier_label} · {s.paid_at ? new Date(s.paid_at).toLocaleDateString() : ""}</p>
                    </div>
                    <div className="text-right">
                      <div className="font-bold text-emerald-300">{formatUSD(s.producer_net_usd)}</div>
                      <div className="text-[10px] text-gray-500">gross {formatUSD(s.price_usd)}</div>
                    </div>
                  </div>
                ))}
              </Card>
            ) : (
              <Card className="p-6 text-center text-gray-500 bg-gray-950/40 border-gray-800">No sales yet — your first one is coming.</Card>
            )}
          </section>

          {/* TOP BEATS */}
          <section data-testid="top-beats-section">
            <h2 className="text-xl font-bold text-white mb-3">Top beats by plays</h2>
            {d.top_beats?.length ? (
              <Card className="bg-gray-950/60 border-purple-500/20 divide-y divide-white/5">
                {d.top_beats.map((b) => (
                  <Link key={b.beat_id} to={`/beats/${b.beat_id}`} className="p-3 flex items-center gap-3 hover:bg-white/5" data-testid={`top-beat-${b.beat_id}`}>
                    <div className="w-12 h-12 rounded-md overflow-hidden bg-purple-900/30 flex-shrink-0">
                      {b.cover_url ? <img src={optimizeImage(b.cover_url, 'thumb')} alt={b.title} className="w-full h-full object-cover" onError={(e) => { e.target.src = "/rgm-logo.png"; }} /> : <div className="w-full h-full flex items-center justify-center text-xl">🎹</div>}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-white font-semibold truncate">{b.title}</p>
                      <p className="text-[11px] text-gray-400">{(b.plays || 0).toLocaleString()} plays</p>
                    </div>
                  </Link>
                ))}
              </Card>
            ) : (
              <Card className="p-6 text-center text-gray-500 bg-gray-950/40 border-gray-800">Upload your first beat to see analytics here.</Card>
            )}
          </section>
        </div>
      </main>
    </div>
  );
};

export default ProducerDashboardPage;
